import type {
  MoveWorkPackage,
  WorkPackageStatus,
  WorkPackageWithTasksPublic,
  WorkTaskStatus,
} from "@common/client"
import { formatCount } from "@common/lib/number"

export type NextStep = {
  action: MoveWorkPackage["action"]
  label: string
  blocked: string | null
}

const FINISHED: WorkTaskStatus[] = [
  "complete",
  "signed_off",
  "deferred",
  "closed",
]

export const SIGNED_OFF_STATUSES: readonly WorkTaskStatus[] = [
  "signed_off",
  "deferred",
  "closed",
]

function unsignedTasks(workOrder: WorkPackageWithTasksPublic): number {
  return (workOrder.tasks ?? []).filter(
    (task) => !SIGNED_OFF_STATUSES.includes(task.status),
  ).length
}

function unscheduled(workOrder: WorkPackageWithTasksPublic): string | null {
  return workOrder.arrival_at && workOrder.planned_departure_at
    ? null
    : "Set the arrival and planned departure first"
}

export function taskCount(count: number): string {
  return `${formatCount(count)} ${count === 1 ? "task" : "tasks"}`
}

function tasksBlocking(workOrder: WorkPackageWithTasksPublic): string | null {
  const unsigned = unsignedTasks(workOrder)
  return unsigned > 0 ? `${taskCount(unsigned)} not signed off` : null
}

export function nextStepOf(
  workOrder: WorkPackageWithTasksPublic,
): NextStep | null {
  switch (workOrder.status) {
    case "draft":
      return {
        action: "release",
        label: "Release",
        blocked: unscheduled(workOrder),
      }
    case "scheduled":
      return { action: "start", label: "Start work", blocked: null }
    case "in_progress":
      return {
        action: "complete",
        label: "Complete",
        blocked: tasksBlocking(workOrder),
      }
    case "complete":
    case "closed":
    case "cancelled":
      return null
  }
}

export function unclosedTasks(workOrder: WorkPackageWithTasksPublic): number {
  return (workOrder.tasks ?? []).filter((task) => task.status !== "closed")
    .length
}

export function canReopen(status: WorkPackageStatus): boolean {
  return status === "complete"
}

export function canCancel(status: WorkPackageStatus): boolean {
  return (
    status === "draft" || status === "scheduled" || status === "in_progress"
  )
}

export function isBookable(
  workOrder: WorkPackageStatus,
  task: WorkTaskStatus,
): boolean {
  return (
    (workOrder === "scheduled" || workOrder === "in_progress") &&
    !FINISHED.includes(task)
  )
}

export function isSettled(status: WorkPackageStatus): boolean {
  return status === "closed" || status === "cancelled"
}

export function tasksLocked(status: WorkPackageStatus): boolean {
  return isSettled(status) || status === "complete"
}
